// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { Component } from "react";

import PropTypes from "prop-types";

import { classes } from "utils/classes";

import { UploaderField } from "./UploaderField";
import styles from "./UploaderGroup.scss";
import { UploaderHeader } from "./UploaderHeader";

/** This component manages multiple `<UploaderField>` components. */
export class UploaderGroup extends Component {
  static propTypes = {
    fieldName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    info: PropTypes.string,
    accept: PropTypes.arrayOf(PropTypes.string).isRequired,
    required: PropTypes.bool,
    imageValidationMetadata: PropTypes.shape({
      minWidth: PropTypes.number.isRequired,
      minHeight: PropTypes.number.isRequired,
      maxWidth: PropTypes.number,
      maxHeight: PropTypes.number,
      maxSize: PropTypes.number.isRequired,
      validateAspectRatio: PropTypes.bool,
    }),
    preprocess: PropTypes.func,
    onPreviewClick: PropTypes.func,
    onRemove: PropTypes.func,
    currentlyPreviewedId: PropTypes.number,
    onNewItem: PropTypes.func,
    dataTestId: PropTypes.string,
    look: PropTypes.oneOf(["none", "simple", "big"]),
    tooltip: PropTypes.string,
    // from @Formik
    getFieldMeta: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
  };

  static defaultProps = {
    required: true,
    dataTestId: "uploader-group",
  };

  onSuccess = (idx) => () => {
    const { onNewItem } = this.props;

    if (onNewItem) {
      onNewItem(idx);
    }
  };

  handleOnRemove = (idx, onRemove, setFieldTouched) => {
    const { fieldName } = this.props;
    onRemove(idx);
    setFieldTouched(`${fieldName}[${0}].url`);
  };

  render() {
    const {
      label,
      tooltip,
      accept,
      fieldName,
      required,
      info,
      currentlyPreviewedId,
      look,
      dataTestId,
      getFieldMeta,
      setFieldTouched,
      preprocess,
      imageValidationMetadata,
      onRemove,
      onPreviewClick,
    } = this.props;

    const { value: fields } = getFieldMeta(fieldName);

    return (
      <div data-test-id={dataTestId}>
        {label ? (
          <UploaderHeader
            text={label}
            field={fields}
            required={required}
            tooltip={tooltip}
          />
        ) : null}

        {(fields ?? []).map((item, idx) => (
          <UploaderField
            // eslint-disable-next-line react/no-array-index-key
            key={`${fieldName}-${idx}`}
            name={`${fieldName}[${idx}]`}
            className={classes({
              [styles.activePreview]: idx === currentlyPreviewedId,
              [styles.inactivePreview]: idx !== currentlyPreviewedId,
            })}
            dataTestId={`${dataTestId}-row-${idx}`}
            info={item.status.addButton ? info : undefined}
            accept={accept}
            preprocess={preprocess}
            imageValidationMetadata={imageValidationMetadata}
            required={false}
            look={look}
            onClick={() => onPreviewClick(idx)}
            onSuccess={this.onSuccess(idx)}
            onRemove={() => this.handleOnRemove(idx, onRemove, setFieldTouched)}
          />
        ))}
      </div>
    );
  }
}
